import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages an age gate dialog.
 * This dialog can only be closed by clicking the "YES" button.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 * @property {HTMLButtonElement} yesButton - The yes button.
 * @property {HTMLButtonElement} noButton - The no button.
 *
 * @extends Component<Refs>
 */
export class AgeGateDialog extends Component {
  requiredRefs = ['dialog', 'yesButton', 'noButton'];

  static STORAGE_KEY = 'artet_age_verified';

  connectedCallback() {
    super.connectedCallback();

    // Check if user has already verified their age
    if (!this.isAgeVerified()) {
      // Show the dialog after a short delay to ensure page is loaded
      setTimeout(() => this.showDialog(), 100);
    }
  }

  /**
   * Check if the user has verified their age in this session
   */
  isAgeVerified() {
    return sessionStorage.getItem(AgeGateDialog.STORAGE_KEY) === 'true';
  }

  /**
   * Mark the user as age verified
   */
  setAgeVerified() {
    sessionStorage.setItem(AgeGateDialog.STORAGE_KEY, 'true');
  }

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    // Lock body scroll
    document.body.style.width = '100%';
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;

    dialog.showModal();

    // Add event listeners to buttons
    this.refs.yesButton.addEventListener('click', this.handleYesClick);
    this.refs.noButton.addEventListener('click', this.handleNoClick);
  }

  /**
   * Closes the dialog (only callable from the YES button).
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    // Remove event listeners
    this.refs.yesButton.removeEventListener('click', this.handleYesClick);
    this.refs.noButton.removeEventListener('click', this.handleNoClick);

    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    // Restore body scroll
    document.body.style.width = '';
    const scrollY = document.body.style.top;
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: parseInt(scrollY) * -1, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');
  };

  /**
   * Handle YES button click
   */
  handleYesClick = () => {
    this.setAgeVerified();
    this.closeDialog();
  };

  /**
   * Handle NO button click
   */
  handleNoClick = () => {
    // Redirect to a different page or show a message
    // For now, we'll just show an alert and keep the modal open
    alert('Sorry, you must be 21 or older to enter this site.');
    // Optionally redirect to another page:
    // window.location.href = 'https://www.responsibility.org/';
  };
}

if (!customElements.get('age-gate-dialog')) {
  customElements.define('age-gate-dialog', AgeGateDialog);
}

// Prevent default dialog close behavior
document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDialogElement &&
        event.target.closest('age-gate-dialog')) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
